import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { 
  Calculator, TrendingUp, DollarSign, Users, Target, 
  ArrowRight, CheckCircle, BarChart3, PieChart, Zap,
  Home, Award, ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';

const SponsorROICalculator = () => {
  const [inputs, setInputs] = useState({
    monthlyBudget: 2000,
    avgClientValue: 15000,
    currentCostPerLead: 150,
    conversionRate: 5,
    giveawayValue: 5000
  });

  const [showResults, setShowResults] = useState(false);

  // Spinser assumptions
  const SPINSER_COST_PER_LEAD = 25; // Estimated cost per qualified lead
  const SPINSER_LEAD_QUALITY_MULTIPLIER = 1.5; // Higher intent = better conversion
  const LEADS_PER_GIVEAWAY = 150; // Average entries per giveaway

  // Calculations
  const currentLeadsPerMonth = Math.floor(inputs.monthlyBudget / inputs.currentCostPerLead);
  const currentClientsPerMonth = Math.floor(currentLeadsPerMonth * (inputs.conversionRate / 100));
  const currentRevenue = currentClientsPerMonth * inputs.avgClientValue;
  const currentROI = ((currentRevenue - inputs.monthlyBudget) / inputs.monthlyBudget * 100);

  // Spinser projections
  const spinserLeadsPerMonth = LEADS_PER_GIVEAWAY;
  const spinserConversionRate = inputs.conversionRate * SPINSER_LEAD_QUALITY_MULTIPLIER;
  const spinserClientsPerMonth = Math.floor(spinserLeadsPerMonth * (spinserConversionRate / 100));
  const spinserRevenue = spinserClientsPerMonth * inputs.avgClientValue;
  const spinserCost = inputs.giveawayValue + 500; // Giveaway + platform fee
  const spinserROI = ((spinserRevenue - spinserCost) / spinserCost * 100);

  // Improvement metrics
  const leadIncrease = ((spinserLeadsPerMonth - currentLeadsPerMonth) / currentLeadsPerMonth * 100);
  const roiImprovement = spinserROI - currentROI;
  const costSavings = inputs.monthlyBudget - spinserCost;

  const handleCalculate = () => {
    setShowResults(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
              <span className="text-white">Spin</span>
              <span className="text-[#FF3B30]">ser</span>
            </Link>
            <Link to="/sponsor-signup">
              <Button className="bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-full px-6">
                Become a Sponsor
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-[#FF3B30]/20 text-[#FF3B30] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Calculator className="w-4 h-4" />
            Sponsor ROI Calculator
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
            See Your Potential Returns
          </h1>
          <p className="text-xl text-neutral-400 max-w-2xl mx-auto">
            Compare your current marketing spend vs. sponsoring high-value giveaways to capture qualified homeowner leads.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Calculator Inputs */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10"
          >
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#FF3B30]" />
              Your Current Marketing
            </h2>

            <div className="space-y-6">
              {/* Monthly Budget */}
              <div>
                <div className="flex justify-between mb-2">
                  <Label className="text-neutral-300">Monthly Marketing Budget</Label>
                  <span className="text-white font-bold">${inputs.monthlyBudget.toLocaleString()}</span>
                </div>
                <Slider
                  value={[inputs.monthlyBudget]}
                  onValueChange={([v]) => setInputs({...inputs, monthlyBudget: v})}
                  min={500}
                  max={10000}
                  step={100}
                  className="py-2"
                />
              </div>

              {/* Avg Client Value */}
              <div>
                <div className="flex justify-between mb-2">
                  <Label className="text-neutral-300">Average Client Value (Commission)</Label>
                  <span className="text-white font-bold">${inputs.avgClientValue.toLocaleString()}</span>
                </div>
                <Slider
                  value={[inputs.avgClientValue]}
                  onValueChange={([v]) => setInputs({...inputs, avgClientValue: v})}
                  min={5000}
                  max={50000}
                  step={1000}
                  className="py-2"
                />
              </div>

              {/* Cost Per Lead */}
              <div>
                <div className="flex justify-between mb-2">
                  <Label className="text-neutral-300">Current Cost Per Lead</Label>
                  <span className="text-white font-bold">${inputs.currentCostPerLead}</span>
                </div>
                <Slider
                  value={[inputs.currentCostPerLead]}
                  onValueChange={([v]) => setInputs({...inputs, currentCostPerLead: v})}
                  min={20}
                  max={500}
                  step={10}
                  className="py-2"
                />
              </div>

              {/* Conversion Rate */}
              <div>
                <div className="flex justify-between mb-2">
                  <Label className="text-neutral-300">Lead-to-Client Conversion Rate</Label>
                  <span className="text-white font-bold">{inputs.conversionRate}%</span>
                </div>
                <Slider
                  value={[inputs.conversionRate]}
                  onValueChange={([v]) => setInputs({...inputs, conversionRate: v})}
                  min={1}
                  max={20}
                  step={1}
                  className="py-2"
                />
              </div>

              {/* Giveaway Value */}
              <div>
                <div className="flex justify-between mb-2">
                  <Label className="text-neutral-300">Giveaway Sponsorship Value</Label>
                  <span className="text-white font-bold">${inputs.giveawayValue.toLocaleString()}</span>
                </div>
                <Slider
                  value={[inputs.giveawayValue]}
                  onValueChange={([v]) => setInputs({...inputs, giveawayValue: v})}
                  min={2500}
                  max={15000}
                  step={500}
                  className="py-2"
                />
              </div>

              <Button 
                onClick={handleCalculate}
                className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium mt-4"
                data-testid="calculate-roi-btn"
              >
                Calculate My ROI
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            {/* Current vs Spinser Comparison */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-4">Monthly Comparison</h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Current */}
                <div className="bg-neutral-800/50 rounded-xl p-4">
                  <p className="text-sm text-neutral-400 mb-1">Current Strategy</p>
                  <div className="space-y-3">
                    <div>
                      <p className="text-2xl font-bold text-white">{currentLeadsPerMonth}</p>
                      <p className="text-xs text-neutral-500">Leads/month</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{currentClientsPerMonth}</p>
                      <p className="text-xs text-neutral-500">Clients/month</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-neutral-400">{currentROI > 0 ? '+' : ''}{currentROI.toFixed(0)}%</p>
                      <p className="text-xs text-neutral-500">ROI</p>
                    </div>
                  </div>
                </div>

                {/* Spinser */}
                <div className="bg-gradient-to-br from-[#FF3B30]/20 to-[#FF3B30]/5 rounded-xl p-4 border border-[#FF3B30]/30">
                  <p className="text-sm text-[#FF3B30] mb-1 font-medium">With Spinser</p>
                  <div className="space-y-3">
                    <div>
                      <p className="text-2xl font-bold text-white">{spinserLeadsPerMonth}</p>
                      <p className="text-xs text-neutral-400">Leads/month</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{spinserClientsPerMonth}</p>
                      <p className="text-xs text-neutral-400">Clients/month</p>
                    </div>
                    <div>
                      <p className="text-xl font-bold text-green-400">+{spinserROI.toFixed(0)}%</p>
                      <p className="text-xs text-neutral-400">ROI</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            {showResults && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                <div className="bg-gradient-to-r from-green-500/20 to-green-500/5 rounded-xl p-5 border border-green-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">+{leadIncrease.toFixed(0)}%</p>
                      <p className="text-sm text-green-400">More Qualified Leads</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-500/20 to-blue-500/5 rounded-xl p-5 border border-blue-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">${spinserRevenue.toLocaleString()}</p>
                      <p className="text-sm text-blue-400">Projected Revenue</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-500/20 to-purple-500/5 rounded-xl p-5 border border-purple-500/30">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                      <Target className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">${SPINSER_COST_PER_LEAD}</p>
                      <p className="text-sm text-purple-400">Cost Per Qualified Lead</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Why Spinser Leads Convert Better */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Why Spinser Leads Convert 1.5x Better
              </h3>
              <ul className="space-y-3">
                {[
                  'High-intent homeowners actively thinking about home projects',
                  'AI-scored leads with property data & timeline',
                  'Exclusive access - not shared with competitors',
                  'Pre-qualified by address and property ownership',
                  'Engaged audience (entered voluntarily, provided real data)'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-neutral-300">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <Link to="/sponsor-signup">
              <Button className="w-full h-14 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium text-lg">
                Start Sponsoring Giveaways
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SponsorROICalculator;
